import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  loadSpecBoardOptions,
  loadSpecCertOptions,
  loadSubSpecCertOptions,
  loadSubSpecCertOptionsByCertification,
} from '~/api/utils';
import { CreatableSelect, FormRow as Row, FormSection as Section } from '~/components';
import { OptionWithIdType } from '~/features/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import { EMPTY_OPTION, EMPTY_OPTION_WITH_ID } from '../../constants';
import { specialtyBoardFormSchema } from '../../schemas';
import {
  SpecBoardOptions,
  SpecCertOptions,
  SubSpecCertOptions,
  TrainingFormValues,
} from '../../types';
import { getTrainingCertificationId } from '../../utils';
import { SpecialtyBoardFormValues } from './types';
import { WizardFooter } from './WizardFooter';

interface Props {
  currentPage: number;
  form: Partial<TrainingFormValues>;
  _specialtyBoardOptions: SpecBoardOptions[];
  _specCertOptions: SpecCertOptions[];
  _specSubCertOptions: SubSpecCertOptions[];
  setSpecBoardOptions: (values: OptionWithIdType[]) => void;
  setSpecCertOptions: (values: { id: string; label: string; value: string }[]) => void;
  setSubSpecCertOptions: (values: { id: string; label: string; value: string }[]) => void;
  wizardNext: (values: Partial<TrainingFormValues>) => void;
  wizardPrev: () => void;
  wizardSubmit: (values: Omit<TrainingFormValues, 'id'>) => void;
  closeWizard: () => void;
}

const TAGS = CT.academics.training.wizard;

export const SpecialtyBoardForm = (props: Props) => {
  const {
    form,
    _specialtyBoardOptions,
    _specCertOptions,
    _specSubCertOptions,
    setSpecBoardOptions,
    setSpecCertOptions,
    setSubSpecCertOptions,
  } = props;

  const methods = useCustomForm<SpecialtyBoardFormValues>({
    resolver: zodResolver(specialtyBoardFormSchema),
    defaultValues: {
      specialtyBoard: form.specialtyBoard,
      specialtyCertification: form.specialtyCertification,
      subSpecialtyCertification: form.subSpecialtyCertification,
    },
  });
  const { watch, setValue } = methods;
  const medicalBoardId = form.medicalBoard?.id;
  useEffect(() => {
    let active = true;
    setSpecBoardOptions([]);

    if (medicalBoardId) {
      loadSpecBoardOptions(medicalBoardId).then((options) => {
        if (active) setSpecBoardOptions(options);
      });
    }

    return () => {
      active = false;
    };
  }, [medicalBoardId, setSpecBoardOptions]);

  const specBoard = watch('specialtyBoard');
  const specBoardId = specBoard?.id;
  useEffect(() => {
    let active = true;
    setSpecCertOptions([]);

    if (specBoardId) {
      loadSpecCertOptions(specBoardId).then((options) => {
        if (active) setSpecCertOptions(options);
      });
    }

    return () => {
      active = false;
    };
  }, [specBoardId, setSpecCertOptions]);

  const specialty = watch('specialtyCertification');
  const specialtyCertificationId = getTrainingCertificationId(specialty)?.toString();
  useEffect(() => {
    let active = true;
    setSubSpecCertOptions([]);
    const request = specialtyCertificationId
      ? loadSubSpecCertOptionsByCertification(specialtyCertificationId)
      : specBoardId
        ? loadSubSpecCertOptions(specBoardId)
        : undefined;
    request?.then((options) => {
      if (active) setSubSpecCertOptions(options);
    });
    return () => {
      active = false;
    };
  }, [specialtyCertificationId, specBoardId, setSubSpecCertOptions]);

  const resetSpecialtySelections = () => {
    setValue('specialtyCertification', EMPTY_OPTION_WITH_ID);
    setValue('subSpecialtyCertification', EMPTY_OPTION);
  };

  const handleSpecialtyBoardCreate = (inputValue: string) => {
    setValue('specialtyBoard', { label: inputValue, value: 'userAdded' });
    resetSpecialtySelections();
  };

  const handleSpecialtyCreate = (inputValue: string) => {
    setValue('specialtyCertification', { label: inputValue, value: 'userAdded' });
    setValue('subSpecialtyCertification', EMPTY_OPTION);
  };

  const handleSubSpecialtyCreate = (inputValue: string) => {
    setValue('subSpecialtyCertification', { label: inputValue, value: 'userAdded' });
  };

  return (
    <FormProvider {...methods}>
      <Section componentsPerLine={1}>
        <Row>
          <CreatableSelect
            label={'Specialty Board*'}
            description="Enter or select a specialty board"
            name={'specialtyBoard'}
            cyTag={TAGS.specBoardSelect}
            options={_specialtyBoardOptions}
            onCreateOption={handleSpecialtyBoardCreate}
            onChange={(option) => {
              if (option) {
                resetSpecialtySelections();
              }
            }}
          />
        </Row>
        <Row>
          <CreatableSelect
            key={`specialty-cert-${specialty?.value}`}
            label={'Specialty Certification*'}
            description="Enter or select a specialty, to select specialty board must be selected first"
            name={'specialtyCertification'}
            cyTag={TAGS.specCertSelect}
            onCreateOption={handleSpecialtyCreate}
            onChange={() => setValue('subSpecialtyCertification', EMPTY_OPTION)}
            options={_specCertOptions}
          />
        </Row>
        <Row>
          <CreatableSelect
            key={`sub-specialty-cert-${specBoardId}:${specialtyCertificationId}`}
            label={'Subspecialty Certification'}
            name={'subSpecialtyCertification'}
            description={'Enter or select a subspecialty (Optional)'}
            cyTag={TAGS.subSpecCertSelect}
            options={_specSubCertOptions}
            onCreateOption={handleSubSpecialtyCreate}
          />
        </Row>
        <WizardFooter {...props} />
      </Section>
    </FormProvider>
  );
};
